import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  ErrorState,
  Field,
  Input,
  LoadingState,
  useToast,
} from '@/components/ui';
import { formatRM, parseRM, senToInput } from '@/features/billing/money';
import { useSaveVideoFee, useVideoFee } from '@/features/video-consults/api';
import type { VideoFee } from '@/features/video-consults/types';
import { isApiError } from '@/lib/api';
import styles from '@/pages/queue/queue.module.css';
import { LabFailureBox } from './LabFailureBox';
import type { Failure } from './lab-package-format';
import own from './LabPackages.module.css';

/** The API's limit (SetVideoFeeDto: 0 to 100 000 sen). */
const MAX_FEE_SEN = 100_000;

/**
 * RL-1900 (decision D1, RL-1907): the video consult fee under Services &
 * prices. One fee for every branch (`PUT /video-consults/fee`,
 * services:manage); empty = not set, so patients cannot ask for a video
 * consult. The member price is set under Discount schemes (API RL-1901).
 */
export function VideoFeeCard({ can }: { can: boolean }) {
  const { t } = useTranslation();
  const q = useVideoFee();
  return (
    <Card title={t('videoFee.title')}>
      {q.isLoading ? (
        <LoadingState label={t('videoFee.title')} />
      ) : q.error || !q.data ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <FeeForm key={JSON.stringify(q.data)} can={can} fee={q.data} />
      )}
    </Card>
  );
}

function FeeForm({ can, fee }: { can: boolean; fee: VideoFee }) {
  const { t } = useTranslation();
  const save = useSaveVideoFee();
  const toast = useToast();
  const [value, setValue] = useState(
    fee.priceSen === null ? '' : senToInput(fee.priceSen),
  );
  const [error, setError] = useState<string | undefined>();
  const [failure, setFailure] = useState<Failure | null>(null);

  async function submit() {
    setFailure(null);
    const priceSen = value.trim() ? parseRM(value) : null;
    if (value.trim() && (priceSen === null || priceSen > MAX_FEE_SEN)) {
      setError(t('videoFee.invalid'));
      return;
    }
    setError(undefined);
    try {
      // The member price lives under Discount schemes (API RL-1901).
      await save.mutateAsync({ priceSen });
      toast.show({
        tone: 'success',
        title:
          priceSen === null
            ? t('videoFee.savedOff')
            : t('videoFee.saved', { fee: formatRM(priceSen) }),
      });
    } catch (err) {
      setFailure(
        isApiError(err)
          ? {
              title:
                err.code === 'VIDEO_FEE_INVALID' && fee.memberPriceSen !== null
                  ? t('videoFee.memberAbove', {
                      fee: formatRM(fee.memberPriceSen),
                    })
                  : err.message,
              requestId: err.requestId,
            }
          : { title: 'Could not reach the server. Try again.' },
      );
    }
  }

  return (
    <form
      className={styles.drawer}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {failure && <LabFailureBox failure={failure} />}
      <p className={own.note}>
        {fee.priceSen === null
          ? t('videoFee.notSet')
          : t('videoFee.current', { fee: formatRM(fee.priceSen) })}{' '}
        {fee.memberPriceSen !== null &&
          t('videoFee.member', { fee: formatRM(fee.memberPriceSen) })}{' '}
        {t('videoFee.note')}
      </p>
      <div className={own.grid}>
        <Field
          label={t('videoFee.label')}
          hint={t('videoFee.hint')}
          error={error}
        >
          <Input
            inputMode="decimal"
            disabled={!can}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </Field>
      </div>
      {can && (
        <div>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {t('videoFee.save')}
          </Button>
        </div>
      )}
    </form>
  );
}
